import { SPLIT_BY } from 'routes/BudgetModule/config/constants';
import {
    HISTORIC_AMOUNT,
    SIMULATED_AMOUNT,
    PAY_COMPONENT,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/helpers/constants';
import { PayComponent } from '../PayState/helpers/constants';
import {
    ABSENCE_COST,
    ABSENCE_COUNT,
    ABSENCE_REASON,
    ACCOUNTABLE_STANDARD_CODE,
    AGENT_FIRST_NAME,
    AGENT_LAST_NAME,
    AGENT_SOFTWARE_ID,
    COMP_HOURS_COST,
    COMP_HOURS_COUNT,
    ECHELON_DURATION,
    ECHELON_FIELD,
    ECHELON_FILIERE,
    ECHELON_GRADE,
    ECHELON_SENIORITY,
    ETP_FIELD,
    EXTRA_HOURS_COST,
    EXTRA_HOURS_COUNT,
    INDICIAIRE,
    JOB_LABEL,
    JOB_NUMBER,
    MANTY_STATUS,
    ORG_CHART_HR_MAPPING,
    ORG_CHART_HR_NAME,
    ORG_CHART_HR_SOFTWARE_ID,
    PERMANENT,
    POSITION_ADMIN,
    POSITION_LABEL,
    POSITION_NUMBER,
    RECURRENCE,
    RETIREMENT_SYSTEM,
    SALARY_RAW_CHARGED_FIELD,
    TOTAL_HOURS_COST,
    TOTAL_HOURS_COUNT,
    VACANT_POSITION_END_DATE,
    VACANT_POSITION_LABEL,
    VACANT_POSITION_NUMBER,
    VACANT_POSITION_START_DATE,
} from './definitions';

type ResponseColumn = {
    key: string;
    type?: string;
};

export type Response<T> = {
    data: T[];
    columns: ResponseColumn[];
};

// TODO: implement a more precise type for this
export type DateString = `${number}${number}${number}`;
export type DashDateString = `${number}-${number}-${number}`;
type DatedField<T extends string> = PivotedField<T, DateString>;
type DashDatedField<T extends string> = PivotedField<T, DashDateString>;

type DatedSalaryRawChargedField = DatedField<typeof SALARY_RAW_CHARGED_FIELD>;
type DatedEtpField = DatedField<typeof ETP_FIELD>;

export type SalaryRawChargedFieldByPayComponentAndMonth = DashDatedField<
    PivotedField<typeof SALARY_RAW_CHARGED_FIELD, PayComponent>
>;

export enum JobPermanent {
    Disponibilité = 'Disponibilité',
    Mixte = 'mixte',
    NonPermanent = 'non permanent',
    Permanent = 'permanent',
    Unmapped = 'unmapped',
}

type SalaryMassLineBase = {
    [ORG_CHART_HR_MAPPING]: string;
    [ORG_CHART_HR_NAME]: string;
    [ORG_CHART_HR_SOFTWARE_ID]: string;
    [AGENT_FIRST_NAME]: string;
    [AGENT_LAST_NAME]: string;
    [AGENT_SOFTWARE_ID]: string;
    [PERMANENT]: JobPermanent;
    [POSITION_LABEL]: string;
    [POSITION_ADMIN]: string;
};

export type SalaryMassLine = SalaryMassLineBase &
    Record<DatedSalaryRawChargedField | DatedEtpField, number>;

export type PayComponentSalaryMassLine = SalaryMassLineBase &
    Record<SalaryRawChargedFieldByPayComponentAndMonth, number>;

type DatedExtraHoursCost = DatedField<typeof EXTRA_HOURS_COST>;
type DatedExtraHoursCount = DatedField<typeof EXTRA_HOURS_COUNT>;
type DatedCompHoursCost = DatedField<typeof COMP_HOURS_COST>;
type DatedCompHoursCount = DatedField<typeof COMP_HOURS_COUNT>;
type DatedTotalHoursCost = DatedField<typeof TOTAL_HOURS_COST>;
type DatedTotalHoursCount = DatedField<typeof TOTAL_HOURS_COUNT>;

type SupCompHoursBase = {
    [ORG_CHART_HR_NAME]: string;
    [ORG_CHART_HR_MAPPING]: string;
    [ORG_CHART_HR_SOFTWARE_ID]: string;
    [AGENT_FIRST_NAME]: string;
    [AGENT_LAST_NAME]: string;
    [AGENT_SOFTWARE_ID]: string;
};

export type SupCompHoursLine = SupCompHoursBase &
    Record<
        | DatedExtraHoursCost
        | DatedExtraHoursCount
        | DatedCompHoursCost
        | DatedCompHoursCount
        | DatedTotalHoursCost
        | DatedTotalHoursCount,
        number
    >;

type DatedAbsenceCost = DatedField<typeof ABSENCE_COST>;
type DatedAbsenceCount = DatedField<typeof ABSENCE_COUNT>;
type AbsenceBase = {
    [ORG_CHART_HR_MAPPING]: string;
    [ORG_CHART_HR_NAME]: string;
    [ORG_CHART_HR_SOFTWARE_ID]: string;
    [AGENT_FIRST_NAME]: string;
    [AGENT_LAST_NAME]: string;
    [AGENT_SOFTWARE_ID]: string;
    [ABSENCE_REASON]: string;
};

export type Absence = AbsenceBase &
    Record<DatedAbsenceCost | DatedAbsenceCount, number>;

export type VacantPosition = {
    [ORG_CHART_HR_MAPPING]: string;
    [ORG_CHART_HR_NAME]: string;
    [ORG_CHART_HR_SOFTWARE_ID]: string;
    [VACANT_POSITION_LABEL]: string;
    [VACANT_POSITION_NUMBER]: string;
    [VACANT_POSITION_START_DATE]: DateString | '';
    [VACANT_POSITION_END_DATE]: DateString | '';
};

export type Echelon = {
    [ORG_CHART_HR_MAPPING]: string;
    [ORG_CHART_HR_NAME]: string;
    [ORG_CHART_HR_SOFTWARE_ID]: string;
    [AGENT_FIRST_NAME]: string;
    [AGENT_LAST_NAME]: string;
    [AGENT_SOFTWARE_ID]: string;
    [MANTY_STATUS]: string;
    [ECHELON_FIELD]: string;
    [POSITION_NUMBER]: number;
    [ECHELON_GRADE]: string;
    [ECHELON_FILIERE]: string;
    [ECHELON_SENIORITY]: number;
    [ECHELON_DURATION]: number;
};
type GVTDataBase = {
    [ORG_CHART_HR_MAPPING]: string;
    [ORG_CHART_HR_SOFTWARE_ID]: string;
    [ORG_CHART_HR_NAME]: string;
    [AGENT_FIRST_NAME]: string;
    [AGENT_LAST_NAME]: string;
    [AGENT_SOFTWARE_ID]: string;
    [RECURRENCE]: string;
    [PAY_COMPONENT]: string;
};

type SimulationResultDataBase = {
    [ORG_CHART_HR_MAPPING]: string;
    [ORG_CHART_HR_NAME]: string;
    [ORG_CHART_HR_SOFTWARE_ID]: string;
    [AGENT_FIRST_NAME]: string;
    [AGENT_LAST_NAME]: string;
    [AGENT_SOFTWARE_ID]: string;
    [RECURRENCE]: string;
    [PAY_COMPONENT]: string;
    [MANTY_STATUS]: string;
    [INDICIAIRE]: string;
    [RETIREMENT_SYSTEM]: string;
    [JOB_LABEL]: string;
    [JOB_NUMBER]: string;
};

export type NatureResultData = SimulationData<{
    [ACCOUNTABLE_STANDARD_CODE]: string;
    [ORG_CHART_HR_NAME]?: string;
}>;

type DatedHistoricAmount = DatedField<typeof HISTORIC_AMOUNT>;
type DatedSimulatedAmount = DatedField<typeof SIMULATED_AMOUNT>;

export function isDatedField<T extends string>(
    datedField: string,
    field: T,
): datedField is DatedField<T> {
    return datedField.startsWith(`${field}${SPLIT_BY}`);
}
export const getYearFromDateString = (date: DateString): number =>
    parseInt(date.slice(0, 4), 10);

export const getUniqueYearsFromDateStrings = (dates: DateString[]): number[] =>
    Array.from(new Set(dates.map(getYearFromDateString)));

export type HistoricData<T extends object> = T &
    Record<DatedHistoricAmount, number>;

export type SimulationOnlyData = Record<DatedSimulatedAmount, number>;

export type SimulationData<T extends object> = HistoricData<T> &
    Record<DatedSimulatedAmount, number>;

export type GVTData = SimulationData<GVTDataBase>;

export type SimulationResultData = SimulationData<SimulationResultDataBase>;
